/**
 * Spread-over rules — the adapter's side (Ledger rules › Spread over; the rules are the purchase ledger's, read from
 * `/views/spread-over-rules` and written to the URL that read carries). Each method runs over an `AccrualsTransport`:
 * the real adapter's is the API client, the mock adapter's is the mock ledger
 * (`mock-data/accruals-and-prepayments.ts`), which answers the same URLs with the same wire shapes. So both adapters
 * share one mapping and one way of building every request, and the contract suite proves they agree.
 *
 * The wire is snake_case; the shapes in `accruals-and-prepayments-types.ts` are camelCase. The month end, its
 * prepayments and its recurring accruals are One objects and have no method here.
 */

import type { AccrualsWire } from "../api-types";
import type {
  SpreadOverRule,
  SpreadOverRuleListOptions,
  SpreadOverRulePage,
  SpreadOverRuleScope,
  SpreadOverRuleSubject,
  SpreadOverRuleVersion,
  WriteSpreadOverRuleInput,
} from "../accruals-and-prepayments-types";
import { assertRelativeActionUrl } from "../order-to-invoice-actions";
import { extractFieldErrors } from "../validation";
import { ApiError } from "./errors";

type Schemas = AccrualsWire;

/** How the adapter reaches the ledger: the API client, or the mock ledger. */
export interface AccrualsTransport {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body: unknown): Promise<T>;
}

// ── Errors ─────────────────────────────────────────────────────────────────

/**
 * The ledger's 409s and 404s carry `detail = {code, message, ...}`; its 400s
 * the field-tagged list (.claude/rules/api-validation.md). Re-throw with the
 * business message as the error's message, keeping `body` so a page can bind
 * field errors to inputs and branch on the code.
 */
function inWords(err: unknown): unknown {
  if (!(err instanceof ApiError)) return err;
  const fieldMessages = extractFieldErrors(err).map((e) => e.message);
  if (fieldMessages.length > 0) {
    return new ApiError(
      err.status,
      err.statusText,
      fieldMessages.join(" "),
      err.body,
    );
  }
  const message = detailOf(err)?.message;
  return typeof message === "string"
    ? new ApiError(err.status, err.statusText, message, err.body)
    : err;
}

function detailOf(err: ApiError): Record<string, unknown> | null {
  const detail = (err.body as { detail?: unknown } | null)?.detail;
  return detail && typeof detail === "object" && !Array.isArray(detail)
    ? (detail as Record<string, unknown>)
    : null;
}

async function inWordsOnFailure<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (err) {
    throw inWords(err);
  }
}

/** What an error says to a person: the business message when there is one. */
export function accrualsErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const message = detailOf(err)?.message;
    if (typeof message === "string") return message;
    const fields = extractFieldErrors(err);
    if (fields.length > 0) return fields.map((f) => f.message).join(" ");
    return err.detail || fallback;
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

// ── Mapping ────────────────────────────────────────────────────────────────

// ── Mapping ────────────────────────────────────────────────────────────────

function mapSpreadOverRuleVersion(
  wire: Schemas["SpreadOverRuleVersionOut"],
): SpreadOverRuleVersion {
  return {
    isSpreadOver: wire.is_spread_over,
    reason: wire.reason,
    setByName: wire.set_by_name,
    setAt: wire.set_at,
  };
}

function mapSpreadOverRule(wire: Schemas["SpreadOverRuleOut"]): SpreadOverRule {
  return {
    id: wire.id,
    scope: wire.scope,
    scopeLabel: wire.scope_label,
    subjectKey: wire.subject_key,
    subjectLabel: wire.subject_label,
    supplierNo: wire.supplier_no,
    current: mapSpreadOverRuleVersion(wire.current),
    earlierVersions: wire.earlier_versions.map(mapSpreadOverRuleVersion),
    purchaseInvoicesLast12Months: wire.purchase_invoices_last_12_months,
    overriddenLast12Months: wire.overridden_last_12_months,
  };
}

function paged<W, T>(
  wire: {
    items: W[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  },
  map: (item: W) => T,
) {
  return {
    items: wire.items.map(map),
    total: wire.total,
    page: wire.page,
    pageSize: wire.page_size,
    totalPages: wire.total_pages,
  };
}

// ── Requests ───────────────────────────────────────────────────────────────

function query(params: Record<string, string | number | string[] | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    for (const one of Array.isArray(value) ? value : [value]) {
      search.append(key, String(one));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

// ── The adapter's methods, over a transport ─────────────────────────────────

export function accrualsApi(transport: AccrualsTransport) {
  const post = <T>(path: string, body: unknown) =>
    inWordsOnFailure(() => transport.post<T>(path, body));

  return {
    // ── Spread-over rules: the purchase ledger's (Ledger rules › Spread over) ──

    async listSpreadOverRules(
      options: SpreadOverRuleListOptions = {},
    ): Promise<SpreadOverRulePage> {
      const wire = await inWordsOnFailure(() =>
        transport.get<Schemas["SpreadOverRuleListResponse"]>(
          `/views/spread-over-rules${query({
            scope: options.scope,
            is_spread_over:
              options.isSpreadOver === undefined
                ? undefined
                : String(options.isSpreadOver),
            q: options.q?.trim() || undefined,
            sort: options.sort,
            page: options.page,
            page_size: options.pageSize,
          })}`,
        ),
      );
      return {
        ...paged(wire, mapSpreadOverRule),
        spreadOverRulesUrl: wire.spread_over_rules_url,
        canManageRules: wire.can_manage_rules,
      };
    },

    async searchSpreadOverRuleSubjects(input: {
      scope: SpreadOverRuleScope;
      q?: string;
    }): Promise<SpreadOverRuleSubject[]> {
      const wire = await inWordsOnFailure(() =>
        transport.get<Schemas["SpreadOverRuleSubjectOut"][]>(
          `/views/spread-over-rule-subjects${query({
            scope: input.scope,
            q: input.q?.trim() || undefined,
          })}`,
        ),
      );
      return wire.map((s) => ({ key: s.key, label: s.label }));
    },

    /** A new version, posted to the URL the rules read carried; re-read what shows the rules afterwards. */
    async writeSpreadOverRule(
      spreadOverRulesUrl: string,
      input: WriteSpreadOverRuleInput,
    ): Promise<void> {
      await post<unknown>(assertRelativeActionUrl(spreadOverRulesUrl), {
        scope: input.scope,
        subject_key: input.subjectKey,
        is_spread_over: input.isSpreadOver,
        reason: input.reason,
      });
    },
  };
}

export type AccrualsApi = ReturnType<typeof accrualsApi>;
